/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  VictoryArea,
  VictoryChart,
  VictoryLabel,
  VictoryPolarAxis,
  VictoryScatter,
} from 'victory'
import colors from '@sportninja/common/constants/appColors'
import type { ThisWeekRadarAxis } from '@sportninja/common/features/profile/stats/types'
import {
  buildRadarPointTooltip,
  formatStatLine,
} from '@sportninja/common/features/profile/stats/utils/pointTooltip'
import { getSeriesColor } from '@sportninja/common/features/profile/stats/utils/seriesColors'
import { useState } from 'react'

import ChartPointTooltip from './ChartPointTooltip'
import type { PointHover } from './ProgressPointMarker'
import RadarPointMarker from './RadarPointMarker'
import { getRadarLabelAnchor } from '@sportninja/common/features/profile/stats/utils/radarLabelAnchor'

interface ThisWeekRadarProps {
  axes: ThisWeekRadarAxis[]
  /** The week's games, for the point tooltip's title. */
  gamesPlayed: number
}

interface RadarDatum {
  x: string
  y: number
  /** The spoke's position in `axes`, for the point marker. */
  index: number
}

// Wider than tall: the side labels ('PiM: 2') need more room than the top
// and bottom ones.
const RADAR_WIDTH = 260
const RADAR_HEIGHT = 200
const RADAR_PADDING = { top: 28, bottom: 28, left: 60, right: 60 }
const RING_TICKS = [0.25, 0.5, 0.75, 1]
// First spoke at 12 o'clock, the rest clockwise.
const START_ANGLE = 90
const END_ANGLE = -270

// SVG text otherwise takes Victory's built-in font; `inherit` picks up the
// tenant font from the page. Victory styles take numbers, not css.js presets.
const AXIS_LABEL_STYLE = {
  fill: colors.NEUTRAL_50,
  fontSize: 12,
  fontFamily: 'inherit',
  // Gap between the outer ring and a label.
  padding: 14,
}

const blankTick = (): string => ''

/** The first charted stat's colour (coral on web), so the radar matches it. */
const RADAR_COLOR = getSeriesColor(0, 'dark')

/**
 * The week as a radar: each spoke is a stat, filled out to the player's best
 * per-game period in the loaded range. Labels carry the week's totals; a
 * point shows the same value tooltip as the progress chart.
 */
const ThisWeekRadar = ({ axes, gamesPlayed }: ThisWeekRadarProps) => {
  'use memo'

  const [hovered, setHovered] = useState<PointHover | null>(null)

  const data: RadarDatum[] = axes.map((axis, index) => ({
    x: axis.abbr,
    y: axis.ratio,
    index,
  }))
  const tooltipFor = (index: number) => {
    const axis = axes[index]
    return axis ? buildRadarPointTooltip(axis, gamesPlayed) : null
  }
  const pointLabelFor = (index: number): string =>
    tooltipFor(index)?.value ?? ''
  const hoveredTooltip = hovered ? tooltipFor(hovered.index) : null
  const tickLabelFor = (abbr: string): string => {
    const axis = axes.find((a) => a.abbr === abbr)
    return axis ? formatStatLine(axis.abbr, axis.value) : abbr
  }
  // Victory picks the wrong side for a clockwise-from-top web, so each label
  // is anchored from its own spoke angle to grow away from the chart.
  const labelIndex = (props: { index?: number | string }): number =>
    Number(props.index ?? 0)
  const description = axes
    .map((axis) => `${axis.name}: ${axis.value}`)
    .join(', ')

  return (
    <div css={containerStyles} role='group' aria-label={description}>
      <VictoryChart
        polar
        width={RADAR_WIDTH}
        height={RADAR_HEIGHT}
        padding={RADAR_PADDING}
        startAngle={START_ANGLE}
        endAngle={END_ANGLE}
        domain={{ y: [0, 1] }}
        categories={{ x: axes.map((axis) => axis.abbr) }}
      >
        <VictoryPolarAxis
          dependentAxis
          tickValues={RING_TICKS}
          tickFormat={blankTick}
          style={{
            axis: { stroke: 'none' },
            grid: { stroke: colors.SECONDARY_200, strokeWidth: 1 },
          }}
        />
        <VictoryPolarAxis
          labelPlacement='vertical'
          tickFormat={tickLabelFor}
          tickLabelComponent={
            <VictoryLabel
              textAnchor={(props) =>
                getRadarLabelAnchor(labelIndex(props), axes.length).textAnchor
              }
              verticalAnchor={(props) =>
                getRadarLabelAnchor(labelIndex(props), axes.length)
                  .verticalAnchor
              }
            />
          }
          style={{
            axis: { stroke: colors.SECONDARY_200 },
            grid: { stroke: colors.SECONDARY_200, strokeWidth: 1 },
            tickLabels: AXIS_LABEL_STYLE,
          }}
        />
        <VictoryArea
          data={data}
          style={{
            data: {
              fill: `${RADAR_COLOR}40`,
              stroke: RADAR_COLOR,
              strokeWidth: 2,
            },
          }}
        />
        <VictoryScatter
          data={data}
          dataComponent={
            <RadarPointMarker
              labelFor={pointLabelFor}
              color={RADAR_COLOR}
              onHoverChange={setHovered}
            />
          }
        />
      </VictoryChart>
      {hovered && hoveredTooltip ? (
        // The radar draws at true pixel size, so Victory's units are px.
        <ChartPointTooltip
          tooltip={hoveredTooltip}
          color={RADAR_COLOR}
          left={hovered.x}
          top={hovered.y}
        />
      ) : null}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

/** Also the positioning context for the point tooltip. */
const containerStyles = css`
  position: relative;
  width: ${RADAR_WIDTH}px;
  height: ${RADAR_HEIGHT}px;
  flex-shrink: 0;
`

export default ThisWeekRadar
